class CTOCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CTOCartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEvents = this.addEvents.bind(this);
    this.$footer = this.querySelector('.js-cart-items-footer');
    this.scrollPosition = 0;
    // add mobile scroll event only if apple pay is supported
    if (window?.ApplePaySession && this.$footer.querySelector('.apple-button')) {
      this.$footer.classList.add('apple-pay-supported');
      if (this.$footer.querySelector('.checkout-button')) {
        this.$footer.classList.add('checkout-button-supported');
      }
      this.querySelector('cart-items').addEventListener('scroll', this.onScroll.bind(this));
    }

    this.ctoItemKey = null;
    this.currentItemsSkus = {};
    this.currentItemsAll = {};
  }

  onScroll(event) {
    if (window.innerWidth < 768) {
      if (event.target.scrollTop >= this.scrollPosition) {
        this.$footer.classList.add('scrolled');
      } else {
        this.$footer.classList.remove('scrolled');
      }
      this.scrollPosition = event.target.scrollTop;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // scroll to top of the cart items
      this.querySelector('cart-items').scrollTop = 0;
      this.querySelector('.drawer__inner').scrollTop = 0;
      this.scrollPosition = 0;
      this.$footer.classList.remove('scrolled');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CTOCartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  updateLatestCartItems() {
    const $items = this.querySelectorAll(`.cto-cart-item[data-cto-cart-item-key="${this.ctoItemKey}"]`);

    if ($items?.length) {
      $items.forEach(($item) => {
        $item.classList.add('active');
      });
    }

    // Show insurance section if there are active insurance items
    const $insuranceSection = this.querySelector('.js-cto-insurance')
    const $insuranceItems = $insuranceSection.querySelectorAll(`.cto-cart-item.active`);
    if ($insuranceItems?.length) {
      $insuranceSection.classList.remove('hidden');
    }

    // Show GWP section if there are active items
    const $gwpSection = this.querySelector('.js-cto-gwp')
    const $gwpItems = $gwpSection.querySelectorAll(`.cto-cart-item.active`);
    if ($gwpItems?.length) {
      $gwpSection.classList.remove('hidden');
    }

    const $offerSection = this.querySelector('.js-cto-offers');
    const $offerItems = $offerSection?.querySelector(`.cto-cart-item.active`)|| null;
      if ($offerItems) {
        this.refreshCarouselEvents();
      }

    const $mainCartItems = this.querySelectorAll('.js-cart-items-body > .cart-items .cto-cart-item');
    $mainCartItems.forEach(($item) => {
      $item.classList.remove('active');
    });
    Object.keys(this.currentItemsSkus).forEach((sku) => {
      const $mainCartItemsWrapper = this.querySelector('.js-cart-items-body > .cart-items');
      const $item = $mainCartItemsWrapper.querySelector(`.cto-cart-item[data-sku="${sku}"]`);
      if ($item) {
        $item.classList.add('active');
      }
    });
  }

  updateItems(itemsData) {
    this.updateCurrentItemsSkus(itemsData);
    this.updateCurrentItemsAll(itemsData);
    fetch(`${window.Shopify.routes.root}cart?view=cto-cart-drawer-items`)
    .then((response) => response.text())
    .then((html) => {
      const $items = this.querySelector('.js-cart-items-body') || null;

      if ($items && this.$footer) {
        $items.innerHTML = this.getInnerHTML(html, '.js-cart-items-body');
        this.$footer.innerHTML = this.getInnerHTML(html, '.js-cart-items-footer');
        // get new cto item key from updated liquid
        const newCto = this.querySelector('.cto-cart-item[data-cto-product="true"]');
        const {ctoCartItemKey} = newCto?.dataset || {};
        if (ctoCartItemKey) {
          this.updateCtoItemKey(ctoCartItemKey);
        }
        this.updateLatestCartItems();
        this.addEvents();
        this.open();
      }

      // scroll to top
      this.querySelector('cto-cart-drawer cart-items').scroll(0, 0);
    })
    .catch((error) => console.error({message: 'Error updating items', error: error}));
  }

  updateCurrentItemsSkus(itemsData) {
    const ctoItem = itemsData?.find((item) => item.properties?._cto_product);
    const ctoItemSku = ctoItem?.sku;
    if (!ctoItemSku) {
      itemsData?.forEach((item) => {
        this.currentItemsSkus[item.sku] = true;
      });
    } else {
      this.updateCtoItemKey(ctoItem.key);
      this.currentItemsSkus = {};
      itemsData?.forEach((item) => {
        this.currentItemsSkus[item.sku] = true;
      });
    }
  }

  updateCurrentItemsAll(itemsData) {
    itemsData?.forEach((item) => {
      this.currentItemsAll[item.key] = item;
    });
  }

  updateCtoItemKey(ctoItemKey) {
    this.ctoItemKey = ctoItemKey;
  }

  forceClearCtoItems() {
    this.ctoItemKey = null;
    this.currentItemsSkus = {};
  }

  addEvents() {
    const $modalTriggers = this.querySelectorAll('.ac-modal-trigger') || [];
    const $nmpTriggers = document.querySelectorAll('.js-net-monthly-pricing-modal-trigger');
    if ($modalTriggers?.length) {
      $modalTriggers.forEach(($trigger) => {
        $trigger.addEventListener('click', (event) => {
          if ($trigger.classList.contains('cart-payment-blocks__link') && $nmpTriggers?.length) {
            $nmpTriggers[0].click();
          }
        });
      });
    }
    if (window.refreshModals) window.refreshModals();
  }

  refreshCarouselEvents() {
    const $offerSection = this.querySelector('.js-cto-offers');
    const $offerItems = $offerSection?.querySelector(`.cto-cart-item.active`)|| null;
    const $alsoLikeCarousel = $offerItems?.querySelector('.js-also-like-carousel') || null;
    const $accessoriesCarousel = $offerItems?.querySelector('.js-product-accessories-carousel') || null;

    $offerSection.classList.remove('hidden');
    // small timeout to make sure the carousel is rendered
    setTimeout(() => {
      if ($alsoLikeCarousel) {
        this.addCaouselEvents($alsoLikeCarousel, 'js-you-also-like-carousel');
      }

      if ($accessoriesCarousel) {
        this.addCaouselEvents($accessoriesCarousel, 'js-accessories-carousel');
      }
    }, 100);
  }

  addCaouselEvents(el, selector) {
    if (!el && !selector) return;
    const $atcButtons = el.querySelectorAll('.slider-form:not(.event-attached)');

    if ($atcButtons?.length > 0) {
      window.quickAddEvents();
    }

    $(`.${selector}`).slick({
      slidesToShow: 3,
      slidesToScroll: 1,
      infinite: false,
      variableWidth: true,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 1.45,
            infinite: false,
            variableWidth: false,
          }
        },
      ]
    });
  }
}

customElements.define('cto-cart-drawer', CTOCartDrawer);
